import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { ActionButton, Button, IKeytipProps } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { CommandButton } from "@fluentui/react";
import {
  buttonProperties,
  getNativeProps,
} from "@fluentui/react/lib/Utilities";

import {
  dataUniqueId,
  getInputMethodFromEvent,
  InputMethod,
  isCheckableRole,
  isSelectableRole,
} from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";
import { defaultKeytipExecute } from "@1js/acui-keytip";
import { deriveMenuKeytip } from "@1js/acui-keytip";
import { AppTooltipHost } from "@1js/acui-tooltip";

import { automationTypeWithGalleryButtonSuffix } from "../../utilities/galleryButtonAutomationType";
import type { AppGalleryButtonProps } from "./AppGalleryButton.Props";

export class AppGalleryButton extends React.PureComponent<
  React.PropsWithChildren<AppGalleryButtonProps>,
  {}
> {
  private galleryButton = React.createRef<ActionButton>();
  private galleryButtonWrapper = React.createRef<HTMLDivElement>();
  private tooltipHost: AppTooltipHost | undefined;

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  constructor(props: AppGalleryButtonProps) {
    super(props);
  }

  public render() {
    const {
      id,
      disabled,
      index,
      ariaLabel,
      title,
      selected,
      style,
      role,
      keytipProps,
      uniqueId,
      location,
      contextMenuMap,
      allowDisabledFocus,
      isPreviewGalleryButton,
    } = this.props;
    // Extracting all props from AppGalleryButtonProps except the type, since type is also the native button prop and we want to use that
    const divProps = this.props.focusOnHover
      ? getNativeProps(this.props, buttonProperties, ["type"])
      : undefined;
    return (
      <CommandButton
        {...divProps}
        ref={this.galleryButton}
        elementRef={this.galleryButtonWrapper}
        id={this.getID(id, isPreviewGalleryButton)}
        data-index={index && index}
        disabled={disabled}
        checked={selected}
        aria-selected={isSelectableRole(this.props.role) ? selected : undefined}
        aria-current={!isSelectableRole(this.props.role) ? selected : undefined}
        aria-checked={isCheckableRole(this.props.role) ? selected : undefined}
        role={role && role}
        ariaLabel={ariaLabel && ariaLabel}
        ariaDescription={this.props.customTooltip}
        title={title && title}
        onClick={this.onExecute}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        styles={style}
        data-automation-type={automationTypeWithGalleryButtonSuffix(
          this.props.automationType
        )}
        data-unique-id={dataUniqueId({ id: uniqueId!, location })}
        keytipProps={
          deriveMenuKeytip(keytipProps, this.keytipExecute) as IKeytipProps
        }
        allowDisabledFocus={allowDisabledFocus ?? true}
        data-contextmenu-id={contextMenuMap?.primaryMenuId}
      >
        {this.props.children}
      </CommandButton>
    );
  }

  public componentDidMount() {
    if (this.context.setTooltipElement && this.context.updateTooltipsHidden) {
      const isMenuComponent = this.isGalleryItemInMenuRole();
      this.tooltipHost = new AppTooltipHost({
        updateTooltipsHidden: this.context.updateTooltipsHidden,
        setTooltipElement: this.context.setTooltipElement,
        htmlElement: this.galleryButtonWrapper.current,
        controlId: this.props.id,
        controlLabel: this.props.customTooltip || this.props.label || "",
        tooltipTopMargin: isMenuComponent ? 0 : 3,
        isMenuComponent,
        tooltipDirection: this.props.tooltipDirection,
        performSetOnNextFrame: true,
      });
      this.tooltipHost.registerTooltipHostActions();
    }
  }

  // VSO:5447512 Fix tooltips to be more stable
  private isGalleryItemInMenuRole = (): boolean => {
    return (
      this.props.role === "gridcell" ||
      this.props.role === "menuitem" ||
      this.props.role === "menuitemradio"
    );
  };

  // ADO: 6529976 Fix to have preview and full gallery buttons use different IDs (To resolve: WCAG 4.1.1, duplicate-id-active)
  private getID = (
    id: string,
    isPreviewGalleryButton: boolean | undefined
  ): string => {
    if (!isPreviewGalleryButton) {
      return id;
    }

    const uniquePreviewIdEnabled = checkExperiment(
      this.context,
      ribbonExperiments.appGalleryPreviewButtonIdIsEnabled
    );
    return uniquePreviewIdEnabled ? `${id}-PreviewButton` : id;
  };

  public componentWillUnmount() {
    if (this.tooltipHost) {
      this.tooltipHost.unregisterTooltipHostActions();
      this.tooltipHost = undefined;
    }
  }

  public componentDidUpdate(prevProps: AppGalleryButtonProps) {
    if (
      this.tooltipHost &&
      (prevProps.customTooltip !== this.props.customTooltip ||
        prevProps.label !== this.props.label)
    ) {
      this.tooltipHost.updateTooltip(
        this.props.customTooltip || this.props.label || ""
      );
    }
  }

  private keytipExecute = (executeTarget: HTMLElement) => {
    defaultKeytipExecute(executeTarget, this.executeFromKeytip);
  };

  private onExecute = (ev: React.MouseEvent<Button>) => {
    if (!this.props.disabled && this.props.onExecute) {
      this.props.onExecute({
        galleryButton: this.props,
        location: this.props.location,
        inputMethod: getInputMethodFromEvent(ev),
        shouldTakeFocus: this.props.shouldTakeFocus,
        triggerTime: ev && ev.timeStamp,
        event: ev?.nativeEvent,
        terminalParentControlId: this.props.terminalParentControlId,
      });
    }
  };

  private executeFromKeytip = () => {
    if (!this.props.disabled && this.props.onExecute) {
      this.props.onExecute({
        galleryButton: this.props,
        location: this.props.location,
        inputMethod: InputMethod.Keyboard,
        shouldTakeFocus: this.props.shouldTakeFocus,
        terminalParentControlId: this.props.terminalParentControlId,
      });
    }
  };

  // When focusing a gallery button in an expanded gallery. Call onFocus with the gallery buttons props passed.
  // A gallery button in the one line preview will not
  // have onFocus defined and so will not call anything on focus.
  private onFocus = () => {
    if (this.props.onFocus && !this.props.disabled) {
      this.props.onFocus(this.props);
    }
  };

  private onBlur = () => {
    if (this.props.onBlur && !this.props.disabled) {
      this.props.onBlur(this.props);
    }
  };
}
